/**
 * Domeinwoordenboek — gedeelde POH-GGZ/psychologie-vaktermenlijst (issue #19).
 *
 * Eén bron van waarheid voor drie afnemers:
 *   - Sara (transcriptie/SOEP): zet dictaat-verhaspelingen recht vóór de
 *     SOEP-generatie-call (`agents/kennisagent/src/domeinwoordenboek.py`,
 *     gegenereerd uit dit bestand — zie `shared/scripts/export-domeinwoordenboek.ts`).
 *   - Mila (fuzzy matching op namen/bronnen, CONTEXT.md): kan `lookupEntry`/
 *     `resolveTerm` gebruiken zodra de fuzzy-matcher gebouwd wordt.
 *   - PII-naamdetectie trap 2 op Mila-prompts (issue #31): `PII_ALLOWLIST`
 *     voorkomt dat bekende organisatie-/bron-/vragenlijstnamen een
 *     bevestigingsvraag triggeren (`detectMogelijkeNamen` in
 *     `shared/src/pii.ts`). Een terugkerend vals alarm los je hier op met
 *     een nieuwe entry, niet door de heuristiek op te rekken. Uitzondering:
 *     landen, nationaliteiten en plaatsnamen horen in GEOGRAFISCHE_TERMEN in
 *     `pii.ts` — die gelden bewust niet na een aanhef, waar een plaatsnaam
 *     meestal een achternaam is.
 *
 * Uitbreiden = een entry toevoegen aan `DOMEINWOORDENBOEK` hieronder. Geen
 * codewijziging elders nodig — Python regenereert zijn kopie via
 * `npm run -w @kennisbank/shared build:domeinwoordenboek`.
 */
/**
 * Categorieën die eruit zien als eigennamen en dus een PII-naamdetectie
 * (trap 2) vals-positief kunnen triggeren. `vakterm`/`afkorting` blijven
 * buiten de allowlist — dat zijn geen namen.
 */
const PII_ALLOWLIST_CATEGORIEEN = [
    'organisatie',
    'bron',
    'vragenlijst',
];
export const DOMEINWOORDENBOEK = [
    // --- Afkortingen ---------------------------------------------------
    {
        canoniek: 'SOEP',
        categorie: 'afkorting',
        // "soep" is ook een gewoon Nederlands woord — precies daarom hoort
        // dictaat van "SOEP-verslag" er vaak als het voedingsmiddel uit.
        varianten: ['soep', 'S.O.E.P.', 'soep-verslag'],
    },
    {
        canoniek: 'POH-GGZ',
        categorie: 'afkorting',
        varianten: ['POH GGZ', 'P.O.H.-G.G.Z.', 'pohggz', 'po ggz'],
    },
    {
        canoniek: 'GGZ',
        categorie: 'afkorting',
        varianten: ['G.G.Z.', 'geestelijke gezondheidszorg', 'gee gee zet'],
    },
    {
        canoniek: 'HIS',
        categorie: 'afkorting',
        varianten: ['H.I.S.', 'huisartsinformatiesysteem', 'huisarts informatie systeem'],
    },
    {
        canoniek: 'NHG',
        categorie: 'afkorting',
        varianten: ['N.H.G.', 'Nederlands Huisartsen Genootschap'],
    },
    {
        canoniek: 'EMDR',
        categorie: 'afkorting',
        varianten: ['E.M.D.R.', 'eemdeer', 'EMDR-therapie'],
    },
    {
        canoniek: 'CGT',
        categorie: 'afkorting',
        varianten: ['C.G.T.', 'cee gee tee'],
    },
    {
        canoniek: 'SOLK',
        categorie: 'afkorting',
        varianten: ['somatisch onvoldoende verklaarde lichamelijke klachten', 'solk-klachten'],
    },
    // --- Vragenlijsten ---------------------------------------------------
    {
        canoniek: '4DKL',
        categorie: 'vragenlijst',
        varianten: [
            '4-DKL',
            '4 DKL',
            'vier D K L',
            'viervragenlijst',
            'vierdimensionale klachtenlijst',
        ],
    },
    {
        canoniek: 'PHQ-9',
        categorie: 'vragenlijst',
        varianten: ['PHQ 9', 'PHQ negen', 'P H Q 9', 'PHQ9'],
    },
    {
        canoniek: 'GAD-7',
        categorie: 'vragenlijst',
        varianten: ['GAD 7', 'GAD zeven', 'G A D 7', 'GAD7'],
    },
    {
        canoniek: 'SQ-48',
        categorie: 'vragenlijst',
        varianten: ['SQ 48', 'SQ achtenveertig', 'S Q 48', 'SQ48'],
    },
    // --- Vaktermen -------------------------------------------------------
    {
        canoniek: 'Cognitieve gedragstherapie',
        categorie: 'vakterm',
        varianten: ['cognitatieve gedragstherapie', 'cognitieve gedrags therapie'],
    },
    {
        canoniek: 'Burn-out',
        categorie: 'vakterm',
        varianten: ['burnout', 'burn out', 'burnout-klachten'],
    },
    // --- Organisaties ------------------------------------------------------
    // Eerste seed-mapping, live geobserveerd tijdens de grill-sessie
    // (Wispr Flow-dictaatfout, issue #19 comment).
    {
        canoniek: 'Novadic-Kentron',
        categorie: 'organisatie',
        varianten: ['Novadiskentron', 'Novadic Kentron', 'Nova Diskentron'],
    },
    {
        canoniek: 'Trimbos',
        categorie: 'organisatie',
        varianten: ['Trimbos-instituut', 'Trimbos Instituut', "Trimbo's", 'Trimbosinstituut'],
    },
    {
        canoniek: 'MIND',
        categorie: 'organisatie',
        varianten: ['Mind Korrelatie', 'Mind Landelijk Platform'],
    },
    {
        canoniek: 'PsyQ',
        categorie: 'organisatie',
        varianten: ['Psy Q', 'Psyku', 'Psy-Q'],
    },
    {
        canoniek: 'GGZE',
        categorie: 'organisatie',
        varianten: ['GGZ E', 'GGZ Eindhoven', 'G G Z E'],
    },
    {
        canoniek: 'Jellinek',
        categorie: 'organisatie',
        varianten: ['Jelinek', 'Jellineck'],
    },
    // --- Bronnen -----------------------------------------------------------
    {
        canoniek: 'Thuisarts.nl',
        categorie: 'bron',
        varianten: ['Thuisarts', 'Thuis arts', 'Thuisarts punt nl'],
    },
    {
        canoniek: 'GGZ-standaarden',
        categorie: 'bron',
        varianten: ['GGZ standaarden', 'G.G.Z.-standaarden'],
    },
    {
        // Eigen module — zonder entry zou "staat X in de Sociale Kaart?" een
        // naam-bevestigingsvraag triggeren (issue #31 trap 2).
        canoniek: 'Sociale Kaart',
        categorie: 'bron',
        varianten: ['sociale kaart'],
    },
    {
        // Ring 3-bron (tools-archief); meerwoords eigennaam die anders als
        // persoonsnaam leest.
        canoniek: 'Meesters in de Psychologie',
        categorie: 'organisatie',
        varianten: ['Meesters in de psychologie'],
    },
];
/**
 * Normaliseer een term voor lookup: diacritics gestript, lowercase,
 * punten/apostroffen en liggende streepjes weg, whitespace gecollapsed.
 * Simpele genormaliseerde lookup verslaat een fuzzy-matching-library voor
 * de eerste oplevering (ponytail — geen nieuwe dependency).
 */
export function normalizeTerm(input) {
    return input
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // combining diacritical marks
        .toLowerCase()
        .replace(/[.''`]/g, '')
        .replace(/[-_]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
const _INDEX = new Map();
for (const entry of DOMEINWOORDENBOEK) {
    _INDEX.set(normalizeTerm(entry.canoniek), entry);
    for (const variant of entry.varianten) {
        _INDEX.set(normalizeTerm(variant), entry);
    }
}
/** Exacte (genormaliseerde) lookup van canonieke vorm of variant → entry. */
export function lookupEntry(term) {
    return _INDEX.get(normalizeTerm(term));
}
/** Resolve naar de canonieke schrijfwijze; onbekende term komt ongewijzigd terug. */
export function resolveTerm(term) {
    return lookupEntry(term)?.canoniek ?? term;
}
function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
// Langste variant eerst zodat "POH-GGZ" niet als los "GGZ" wordt herkend
// vóór de langere match een kans krijgt.
// Exacte (niet genormaliseerde) vergelijking: een variant die alleen in
// hoofdlettergebruik van canoniek verschilt (bv. "soep" vs "SOEP") is juist
// een bedoelde correctie, geen no-op.
const _REPLACEMENTS = DOMEINWOORDENBOEK.flatMap((entry) => entry.varianten
    .filter((variant) => variant !== entry.canoniek)
    .map((variant) => ({ variant, canoniek: entry.canoniek })))
    .sort((a, b) => b.variant.length - a.variant.length)
    .map(({ variant, canoniek }) => ({
    // Lookaround i.p.v. \b: variants bevatten vaak punten/streepjes waar
    // \b geen betrouwbare grens geeft (bv. "S.O.E.P." eindigt niet-\w).
    pattern: new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(variant)}(?![\\p{L}\\p{N}])`, 'giu'),
    canoniek,
}));
/**
 * Vervang bekende dictaat-verhaspelingen in vrije tekst door hun canonieke
 * vorm. Gebruikt door Sara vóór de SOEP-generatie-call zodat Gemini de
 * juiste vakterm/organisatienaam ziet in plaats van het transcriptie-
 * gegis van een generiek spraakmodel.
 */
export function applyDomeinwoordenboek(text) {
    if (!text)
        return text;
    let result = text;
    for (const { pattern, canoniek } of _REPLACEMENTS) {
        result = result.replace(pattern, canoniek);
    }
    return result;
}
/**
 * Genormaliseerde allowlist (canoniek + varianten) voor organisatie-/bron-/
 * vragenlijst-categorieën — het aanroeppunt voor de PII-naamdetectie trap 2
 * op Mila-prompts (issue #31), zodra die gebouwd wordt.
 */
export const PII_ALLOWLIST = new Set(DOMEINWOORDENBOEK.filter((entry) => PII_ALLOWLIST_CATEGORIEEN.includes(entry.categorie))
    .flatMap((entry) => [entry.canoniek, ...entry.varianten])
    .map(normalizeTerm));
/** Staat `term` op de PII-allowlist (organisatie/bron/vragenlijst)? */
export function isPiiAllowlisted(term) {
    return PII_ALLOWLIST.has(normalizeTerm(term));
}
//# sourceMappingURL=domeinwoordenboek.js.map